import { useEffect, useState } from 'react'
import { Copy, Check, Wallet, Users, ArrowDownToLine, ExternalLink, Share2, Pencil, Crown, BadgeCheck } from 'lucide-react'
import { useAffiliateStore } from '../stores/useAffiliateStore'
import ReferralShareModal from './ReferralShareModal'

function AffiliateDashboard() {
  const { wallet, transactions, referralCode, referredCount, fetchWallet, fetchTransactions, fetchDownlines, downlines, submitWithdrawal, error } = useAffiliateStore()
  const [showWithdrawal, setShowWithdrawal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [copied, setCopied] = useState(false)
  const [withdrawalForm, setWithdrawalForm] = useState({
    tokens: 100,
    bank_name: '',
    account_name: '',
    account_number: '',
    whatsapp_number: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [editMode, setEditMode] = useState(false)
  const [newCode, setNewCode] = useState('')
  const [savingCode, setSavingCode] = useState(false)
  const [codeError, setCodeError] = useState('')
  const [codeSuccess, setCodeSuccess] = useState('')

  useEffect(() => {
    fetchWallet()
    fetchTransactions()
    fetchDownlines()
  }, [])

  const handleCopy = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleSaveCode = async () => {
    if (!newCode.trim() || newCode.trim().length < 4) {
      setCodeError('Code must be at least 4 characters')
      return
    }
    setSavingCode(true)
    setCodeError('')
    setCodeSuccess('')
    try {
      const res = await fetch('/api/affiliate/set-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await import('../utils/backend').then(m => m.backendClient.auth.getSession()).then(s => s.data.session?.access_token || '')}`
        },
        body: JSON.stringify({ code: newCode.trim().toUpperCase() })
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || 'Failed to set code')
      }
      await res.json()
      setCodeSuccess('Referral code updated!')
      setEditMode(false)
      setNewCode('')
      fetchWallet()
      setTimeout(() => setCodeSuccess(''), 3000)
    } catch (err: any) {
      setCodeError(err.message)
    } finally {
      setSavingCode(false)
    }
  }

  const handleWithdrawal = async () => {
    if (!withdrawalForm.bank_name || !withdrawalForm.account_name || !withdrawalForm.account_number || !withdrawalForm.whatsapp_number) {
      return
    }
    setSubmitting(true)
    try {
      await submitWithdrawal(withdrawalForm)
      setSuccessMsg('Withdrawal request submitted! It will be processed within 24-48 hours.')
      setShowWithdrawal(false)
      setWithdrawalForm({ tokens: 100, bank_name: '', account_name: '', account_number: '', whatsapp_number: '' })
      setTimeout(() => setSuccessMsg(''), 5000)
    } catch {
    } finally {
      setSubmitting(false)
    }
  }

  const referralUrl = referralCode ? `${window.location.origin}/?ref=${referralCode}` : ''

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-black text-white">Affiliate Rewards</h2>
        <p className="mt-1 text-sm text-slate-400">Share your referral code and earn tokens when friends upgrade to Pro.</p>
      </div>

      {successMsg && (
        <div className="rounded-2xl border border-green-500/20 bg-green-500/10 px-4 py-3 text-sm text-green-200">{successMsg}</div>
      )}
      {error && (
        <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="stagger-card animate-fade-in-up rounded-[24px] border border-white/10 bg-[#07141a] p-5 hover:border-green-500/20 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-green-500/10 text-green-400">
              <Wallet size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Token Balance</p>
              <p className="text-2xl font-black text-white">{wallet?.token_balance || 0}</p>
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-500">1 Token = ₦100</p>
        </div>
        <div className="stagger-card animate-fade-in-up rounded-[24px] border border-white/10 bg-[#07141a] p-5 hover:border-blue-500/20 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-400">
              <Users size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Total Earned</p>
              <p className="text-2xl font-black text-white">{wallet?.total_earned || 0}</p>
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-500">{referredCount} referral{referredCount !== 1 ? 's' : ''}</p>
        </div>
        <div className="stagger-card animate-fade-in-up rounded-[24px] border border-white/10 bg-[#07141a] p-5 hover:border-purple-500/20 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-purple-500/10 text-purple-400">
              <ArrowDownToLine size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Withdrawn</p>
              <p className="text-2xl font-black text-white">{wallet?.total_withdrawn || 0}</p>
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-500">₦{(wallet?.total_withdrawn || 0) * 100} total</p>
        </div>
      </div>

      {/* Referral Code */}
      <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-6">
        <h3 className="text-lg font-black text-white">Your Referral Code</h3>
        <p className="mt-1 text-sm text-slate-400">Share this code with friends. When they sign up and upgrade to Pro, you earn 20 tokens (₦2,000).</p>

        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-black/30 px-5 py-3">
            <span className="text-2xl font-black text-[#7cff01] tracking-widest">{referralCode || 'Loading...'}</span>
            <button onClick={handleCopy} className="ml-2 text-slate-400 hover:text-white transition" title="Copy code">
              {copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}
            </button>
          </div>
          <button
            onClick={() => setShowShareModal(true)}
            className="flex items-center gap-2 rounded-2xl bg-[#7cff01] px-5 py-3 text-sm font-black uppercase tracking-widest text-black transition hover:bg-[#8eff1a]"
          >
            <Share2 size={16} />
            Share Link
          </button>
        </div>

        <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
          <ExternalLink size={12} />
          <span>{referralUrl}</span>
        </div>

        {codeSuccess && (
          <div className="mt-3 rounded-2xl border border-green-500/20 bg-green-500/10 px-4 py-2 text-sm text-green-200">{codeSuccess}</div>
        )}
        {codeError && (
          <div className="mt-3 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-2 text-sm text-red-200">{codeError}</div>
        )}

        {!editMode ? (
          <button
            onClick={() => { setEditMode(true); setNewCode(''); setCodeError(''); setCodeSuccess('') }}
            className="mt-4 flex items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-slate-300 transition hover:border-[#7cff01]/30 hover:text-white"
          >
            <Pencil size={14} />
            Edit Referral Code
          </button>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                placeholder="Enter new code (4+ chars)"
                maxLength={12}
                className="flex-1 rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
              />
              <button
                onClick={handleSaveCode}
                disabled={savingCode || !newCode.trim()}
                className="rounded-2xl bg-[#7cff01] px-5 py-3 font-black uppercase tracking-widest text-black transition hover:bg-[#8eff1a] disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
              >
                {savingCode ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => { setEditMode(false); setNewCode(''); setCodeError('') }}
                className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-slate-400 transition hover:text-white"
              >
                Cancel
              </button>
            </div>
            <p className="text-xs text-slate-500">Letters and numbers only. Minimum 4 characters.</p>
          </div>
        )}
      </div>

      {/* Withdraw */}
      <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-black text-white">Withdraw Tokens</h3>
            <p className="mt-1 text-sm text-slate-400">Minimum 100 tokens (₦10,000). Processing takes 24-48 hours.</p>
          </div>
          <button
            onClick={() => setShowWithdrawal(!showWithdrawal)}
            disabled={!wallet || wallet.token_balance < 100}
            className="rounded-2xl bg-[#7cff01] px-5 py-3 text-sm font-black uppercase tracking-widest text-black transition hover:bg-[#8eff1a] disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
          >
            Withdraw
          </button>
        </div>

        {showWithdrawal && (
          <div className="mt-6 space-y-4">
            <div>
              <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">Tokens to Withdraw</label>
              <input
                type="number"
                min={100}
                max={wallet?.token_balance || 0}
                value={withdrawalForm.tokens}
                onChange={(e) => setWithdrawalForm({ ...withdrawalForm, tokens: parseInt(e.target.value) || 100 })}
                className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
              />
              <p className="mt-1 text-xs text-slate-500">You will receive ₦{(withdrawalForm.tokens * 100).toLocaleString()}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">Bank Name</label>
                <input
                  type="text"
                  value={withdrawalForm.bank_name}
                  onChange={(e) => setWithdrawalForm({ ...withdrawalForm, bank_name: e.target.value })}
                  placeholder="e.g. GTBank, Access Bank"
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">Account Name</label>
                <input
                  type="text"
                  value={withdrawalForm.account_name}
                  onChange={(e) => setWithdrawalForm({ ...withdrawalForm, account_name: e.target.value })}
                  placeholder="Account holder name"
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">Account Number</label>
                <input
                  type="text"
                  value={withdrawalForm.account_number}
                  onChange={(e) => setWithdrawalForm({ ...withdrawalForm, account_number: e.target.value })}
                  placeholder="10-digit account number"
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">WhatsApp Number <span className="text-red-400">*</span></label>
                <input
                  type="tel"
                  value={withdrawalForm.whatsapp_number}
                  onChange={(e) => setWithdrawalForm({ ...withdrawalForm, whatsapp_number: e.target.value })}
                  placeholder="+234 xxx xxx xxxx"
                  required
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                />
              </div>
            </div>
            <button
              onClick={handleWithdrawal}
              disabled={submitting || !withdrawalForm.bank_name || !withdrawalForm.account_name || !withdrawalForm.account_number || !withdrawalForm.whatsapp_number}
              className="w-full rounded-2xl bg-[#7cff01] px-4 py-3 font-black uppercase tracking-widest text-black transition hover:bg-[#8eff1a] disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
            >
              {submitting ? 'Submitting...' : 'Submit Withdrawal Request'}
            </button>
          </div>
        )}
      </div>

      {/* Transaction History */}
      <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-6">
        <h3 className="text-lg font-black text-white">Transaction History</h3>
        {transactions.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">No transactions yet. Share your code to start earning!</p>
        ) : (
          <div className="mt-4 space-y-3">
            {transactions.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between rounded-2xl border border-white/5 bg-black/20 px-4 py-3">
                <div>
                  <p className="text-sm text-white">{tx.description || tx.type}</p>
                  <p className="text-xs text-slate-500">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''}</p>
                </div>
                <span className={`text-sm font-black ${tx.tokens > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {tx.tokens > 0 ? '+' : ''}{tx.tokens}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Referral Downlines */}
      <div className="rounded-[24px] border border-white/10 bg-[#07141a] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-black text-white">Your Referrals</h3>
            <p className="mt-1 text-sm text-slate-400">{downlines.length} user{downlines.length !== 1 ? 's' : ''} registered through your link</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 rounded-xl bg-yellow-500/10 text-yellow-400 text-xs font-black">
              {downlines.filter(d => d.subscription?.isPro).length} Pro
            </span>
            <span className="px-3 py-1 rounded-xl bg-green-500/10 text-green-400 text-xs font-black">
              {downlines.filter(d => d.is_verified).length} Verified
            </span>
          </div>
        </div>

        {downlines.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-white/10 rounded-2xl">
            <Users className="mx-auto text-gray-600 mb-3" size={32} />
            <p className="text-gray-400 text-sm">No referrals yet</p>
            <p className="text-xs text-slate-600 mt-1">Share your referral link to start earning</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="pb-2 text-left text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">Name</th>
                  <th className="pb-2 text-left text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">Email</th>
                  <th className="pb-2 text-left text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">Role</th>
                  <th className="pb-2 text-left text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">Status</th>
                  <th className="pb-2 text-left text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">Joined</th>
                </tr>
              </thead>
              <tbody>
                {downlines.map((d) => (
                  <tr key={d.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-2.5 text-white font-medium">{d.full_name || 'Unknown'}</td>
                    <td className="py-2.5 text-slate-400">{d.email}</td>
                    <td className="py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                        d.role === 'scout' ? 'bg-blue-500/10 text-blue-400' :
                        d.role === 'club' ? 'bg-purple-500/10 text-purple-400' :
                        'bg-white/5 text-slate-400'
                      }`}>
                        {d.role || 'player'}
                      </span>
                    </td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-2">
                        {d.is_verified && (
                          <span className="flex items-center gap-1 text-green-400">
                            <BadgeCheck size={12} /> Verified
                          </span>
                        )}
                        {d.subscription?.isPro ? (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 text-[10px] font-black uppercase">
                            <Crown size={10} /> Pro
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-gray-800 text-gray-500 text-[10px] font-black uppercase">
                            Free
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 text-slate-500">{new Date(d.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showShareModal && referralCode && (
        <ReferralShareModal referralCode={referralCode} referralUrl={referralUrl} onClose={() => setShowShareModal(false)} />
      )}
    </div>
  )
}

export default AffiliateDashboard
